import {
  Container,
  Group,
  Text,
  Divider,
  Flex,
  Box,
  Paper,
  Skeleton,
  Button,
  Breadcrumbs,
} from '@mantine/core';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import ReportContext from 'context/ReportContext';
import Recording from './Recording';
import Summary from './Summary';
import Sentiments from './Sentiments';
import Transcripts from './Transcripts';
import SEO from 'components/SEO';

import classes from './index.module.scss';
import { useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { formatPhoneNumber } from 'utils/phone';
import { AccessLevel } from 'interfaces/GetUserResult';
import AuthContext from 'context/AuthContext';
import BillingContext from 'context/BillingContext';
import routes from 'utils/routes';
import { useDeviceType } from 'hooks/useDeviceType';
import { IconChevronLeft } from '@tabler/icons-react';
import { UpgradePopover } from 'components/upgrade';

const CallDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const {
    data: { callDetails, isLoadingCallDetails },
    actions: { getCallDetails },
  } = useContext(ReportContext);
  const {
    data: { account },
  } = useContext(AuthContext);
  const {
    actions: { setPricingOpened },
  } = useContext(BillingContext);
  const { isMobile } = useDeviceType();

  const phoneNumber = callDetails?.participant?.phone_number;
  const isPremium = account?.access_level === AccessLevel.PRO;
  const [showUpgradePopover, setShowUpgradePopover] = useState(!isPremium);
  const upgradeContainerRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!showUpgradePopover) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (upgradeContainerRef.current && 
          !upgradeContainerRef.current.contains(event.target as Node) && 
          !document.querySelector('.mantine-Popover-dropdown')?.contains(event.target as Node)) {
        setShowUpgradePopover(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUpgradePopover]);

  useEffect(() => {
    if (!id || !account?.access_level) {
      return;
    }

    if (!isPremium) {
      // Trigger analytics event for paywall
      if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
        (window as any).analytics.track('reporting_details_paywall_shown');
      }
      return;
    }

    const fetchCallDetails = async () => {
      await getCallDetails(id);
    };

    fetchCallDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isPremium]);

  const handleBackClick = () => {
    const previousSearch = location.state?.dateSearch;

    navigate({
      pathname: routes.reportingPath.href,
      search: previousSearch || searchParams.toString(),
    });
  };

  const handleUpgrade = () => {
    // Close popover
    setShowUpgradePopover(false);
    
    // Open pricing modal using the BillingContext
    setPricingOpened(true);
    
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
      (window as any).analytics.track('reporting_details_upgrade_button_clicked');
    }
  };

  if (!isPremium) {
    return (
      <>
        <SEO 
          title="Call Details | Nucleus AI"
          description="View detailed call information including transcripts, recordings, and analytics."
        />
        <Container
          fluid
          p={0}
          mt={{ base: -16, md: -12 }}
          mb={{ base: 'xs', md: 'md' }}
        >
          <Breadcrumbs
            m={0}
            separator={
              <Text className={classes.breadcrumb} px={2}>
                /
              </Text>
            }
          >
            <Button
              variant="transparent"
              p={0}
              fw={400}
              leftSection={
                <IconChevronLeft
                  size={14}
                  style={{ marginLeft: -4, marginRight: -3 }}
                />
              }
              onClick={handleBackClick}
              className={classes.breadcrumb}
            >
              Reporting
            </Button>
            {!isMobile && (
              <Text c="dimmed" className={classes.breadcrumb}>
                Call details
              </Text>
            )}
          </Breadcrumbs>
        </Container>
        <div 
          ref={upgradeContainerRef}
          style={{ 
            minHeight: '60vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <UpgradePopover
            opened={showUpgradePopover}
            onClose={() => setShowUpgradePopover(false)}
            upgradeTitle="Unlock Call Details & Analysis"
            upgradeDescription="Access complete call records including transcripts, recordings, and detailed analytics. Upgrade to Pro to analyze customer conversations and gather valuable insights."
            onUpgrade={handleUpgrade}
          >
            <div style={{ width: '100%', height: '100%' }}></div>
          </UpgradePopover>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Call Details | Nucleus AI"
        description="View detailed call information including transcripts, recordings, and analytics."
      />
      <Container
        fluid
        p={0}
        mt={{ base: -16, md: -12 }}
        mb={{ base: 'xs', md: 'md' }}
      >
        <Breadcrumbs
          m={0}
          separator={
            <Text className={classes.breadcrumb} px={2}>
              /
            </Text>
          }
        >
          <Button
            variant="transparent"
            p={0}
            fw={400}
            leftSection={
              <IconChevronLeft
                size={14}
                style={{ marginLeft: -4, marginRight: -3 }}
              />
            }
            onClick={handleBackClick}
            className={classes.breadcrumb}
          >
            Reporting
          </Button>
          {!isMobile && (
            <Text c="dimmed" className={classes.breadcrumb}>
              Call details
            </Text>
          )}
          {phoneNumber ? (
            <Text className={classes.breadcrumb}>
              {formatPhoneNumber(phoneNumber)}
            </Text>
          ) : (
            <Skeleton w={98.5} h={15} />
          )}
          {isLoadingCallDetails ? (
            <Skeleton w={85} h={15} />
          ) : (
            <Text className={classes.breadcrumb}>
              {dayjs(callDetails?.start_time).format(
                isMobile ? 'MMM D, YY HH:mm' : 'MMM D, YYYY HH:mm:ss'
              )}
            </Text>
          )}
        </Breadcrumbs>
      </Container>
      <Paper
        radius="md"
        withBorder
        p={0}
        pos={{ md: 'sticky', base: 'relative' }}
        className={classes.scrollContainer}
      >
        <Flex direction={{ base: 'column', lg: 'row' }}>
          <Box className={classes.scrollSection} flex={1}>
            <Group justify="space-between" p={{ base: 'md', lg: 'lg' }}>
              <Recording />
              <Divider my="sm" w="100%" />
              <Summary />

              <Sentiments />
            </Group>
          </Box>

          <Divider
            orientation="vertical"
            style={{ borderColor: 'var(--mantine-color-gray-2)' }}
          />

          <Box className={classes.scrollSection} flex={1}>
            <Group p={{ base: 'md', lg: 'lg' }}>
              <Transcripts />
            </Group>
          </Box>
        </Flex>
      </Paper>
    </>
  );
};

export default CallDetailsPage;
